import React, { useEffect, useState } from 'react'
import classes from './CourseDetails.module.css'
import { ReactComponent as RightIcon } from '../../../assets/icons/right.svg'
import { ReactComponent as LecturesIcon } from '../../../assets/icons/lectures.svg'
import { ReactComponent as StudentsIcon } from '../../../assets/icons/students.svg'
import { ReactComponent as DownloadsIcon } from '../../../assets/icons/downloadable.svg'
import { Button } from '../../../components/Button/Button'
import CourseContent from '../../../components/Accordions/CourseContent'
import { CourseCard } from '../../../components/Cards/GeneralCard/Card'
import Footer from '../../../components/Footer/Footer'
import classNames from 'classnames'
import arrow from '../../../assets/images/angular bracket thing.png'
import { AuthenticatedHeader } from '../../../components/Header/AuthenticatedHeader'
import { useDispatch, useSelector } from 'react-redux'
import { userSelectors } from '../../../redux/userSlice'
import {
    courseDetailsActions,
    courseDetailsSelectors,
} from './courseDetailsSlice'
import {
    convertToFormatted,
    getImageURL,
    getFreeCoursesLeft,
} from '../../../util/functions'
import placeholder from '../../../assets/images/course-placeholder.svg'
import { getCourse } from './courseDetailsApi'
import Loading from '../../../components/loaders/Loading'
import notification from '../../../components/Notification/notification'
import { startUpgradePlanWizard } from '../../Wizard/wizardLogic'
import { addCourseToCart } from '../../Cart/cartApi'
import shieldIcon from '../../../assets/icons/shield.svg'
import {
    BuyNowSnippet,
    EarnPoints,
    FreePrice,
    UserChoices,
} from './CourseDetailsSnippets'
import { cartSelectors } from '../../Cart/cartSlice'
import { recentlyViewedActions } from '../../../redux/recentlyViewedSlice'
import { AccountTypes, CourseTypes } from '../../../util/constants'

const CourseDetails = ({ match, location, history }) => {
    const [userChoice, setUserChoice] = useState('purchase')

    const dispatch = useDispatch()

    const user = useSelector(userSelectors.userDetails)
    const isLoggedIn = useSelector(userSelectors.isLoggedIn)

    const items = useSelector(cartSelectors.cartItems)
    const userFreeCourses = getFreeCoursesLeft({
        items,
        userFreeCourses: user.freeCourses,
    })

    const paidUser =
        String(user.accounttype).toLowerCase() ===
        AccountTypes.Paid?.toLowerCase()
    const subscribedUser =
        String(user.accounttype).toLowerCase() ===
        AccountTypes.Subscriber?.toLowerCase()

    const upgradePlan = () => {
        startUpgradePlanWizard({ returnURL: location.pathname })
    }

    const addToCart = async () => {
        let loader
        if (isLoggedIn) {
            //Only showing a loader when logged in because we has to make an actual API request which will take time unlike just saving to redux state (logged out)
            ;[loader] = await notification.loading({
                title: 'Adding to cart',
            })
        }
        dispatch(
            addCourseToCart({
                id: courseDetails.coursecode,
                course: courseDetails,
                free: false,
            })
        )
            .then(() => {
                loader && loader()
                notification.success({
                    title: `${courseDetails.title} added to cart`,
                })
            })
            .catch(error => {
                notification.destroy()
                if (error?.response?.status === 400) {
                    notification.info({
                        title: error?.response?.data,
                    })
                } else {
                    notification.info({
                        title: 'Failed to add to cart',
                        description: error?.message,
                    })
                }
            })
    }

    const buyNow = async ({ free = false }) => {
        let loader
        if (isLoggedIn) {
            //Only showing a loader when logged in because we has to make an actual API request which will take time unlike just saving to redux state (logged out)
            ;[loader] = await notification.loading({
                title: 'Adding to cart',
            })
        }
        dispatch(
            addCourseToCart({
                id: courseDetails.coursecode,
                course: courseDetails,
                free,
            })
        )
            .then(() => {
                if (isLoggedIn) {
                    loader()
                    history.push('/app/cart')
                } else {
                    history.push('/app/auth/sign-up')
                }
            })
            .catch(error => {
                notification.destroy()
                if (error?.response?.status === 400) {
                    notification.info({
                        title: error?.response?.data,
                    })
                } else {
                    notification.info({
                        title: 'Failed to add to cart',
                        description: error?.message,
                    })
                }
            })
    }

    const isFetching = useSelector(courseDetailsSelectors.isFetching)
    const hasError = useSelector(courseDetailsSelectors.hasError)
    const success = useSelector(courseDetailsSelectors.success)
    const errorMessage = useSelector(courseDetailsSelectors.errorMessage)
    const courseDetails = useSelector(courseDetailsSelectors.courseDetails)

    const premium = String(courseDetails.freeonsubcription) === '0'
    const showStudentCount = String(courseDetails.showstudentcount) === '0'
    let hasFree = false
    if (
        subscribedUser &&
        userFreeCourses > 0 &&
        courseDetails.courseType === CourseTypes.Free_For_Subscribers
    ) {
        hasFree = true
    }

    /*
     * If
     * 1. The user is not logged in
     * 2. The user is a subscribed user that has no free course
     * 3. The course is a premium course
     * AND only show it if it's not a paid user
     * */
    // const showBuyNow =
    //     (!isLoggedIn || (!hasFree && subscribedUser) || premium) && !paidUser

    useEffect(() => {
        window.scrollTo({ left: 0, top: 0, behavior: 'smooth' })
        dispatch(getCourse(match.params.id))

        return () => {
            dispatch(courseDetailsActions.reset())
        }
    }, [match.params.id])

    useEffect(() => {
        if (hasError) {
            notification.failure({
                title: 'Could not fetch course details',
                description: errorMessage,
            })
        }
    }, [hasError])

    useEffect(() => {
        if (success) {
            dispatch(recentlyViewedActions.addCourse(courseDetails))
        }
    }, [success])

    const handleLogin = () => {
        dispatch(
            addCourseToCart({
                id: courseDetails.coursecode,
                course: courseDetails,
                free: false,
            })
        )
            .then(() => {
                notification.destroy()
                notification.success({
                    title: 'Success!',
                    description: `${courseDetails.title} successfully added to cart`,
                })
                history.push(`/app/auth/sign-in`)
                notification.info({
                    title: 'Please sign in',
                })
            })
            .catch(() => {
                notification.destroy()
                notification.failure({
                    title: 'Error!',
                    description: `Something went wrong`,
                })
            })
    }

    return (
        <>
            <AuthenticatedHeader />
            {isFetching ? (
                <Loading inline />
            ) : (
                <main className={classes.mainContent}>
                    <div className={classes.courseNavigation}>
                        <span
                            style={{ cursor: 'pointer' }}
                            onClick={() => history.push(`/app/browse/courses`)}
                        >
                            Courses
                        </span>
                        <RightIcon className={classes.courseNavigationIcon} />
                        {courseDetails.catName}
                    </div>
                    <div className={classes.courseTop}>
                        <img
                            src={
                                courseDetails.thumbnail
                                    ? getImageURL(courseDetails.thumbnail)
                                    : placeholder
                            }
                            alt="Course Thumbnail"
                            className={classes.courseTopImage}
                        />
                        <div className={classes.courseTopText}>
                            <h6 className={classes.courseTopCategory}>
                                {courseDetails.catName}
                            </h6>
                            <h1 className={classes.courseTopCourseName}>
                                {courseDetails.title}
                            </h1>
                            <div className={classes.courseTopStats}>
                                <span className={classes.courseTopStat}>
                                    <LecturesIcon
                                        className={classes.courseTopStatIcon}
                                    />
                                    {courseDetails.numberOfLectures} lectures
                                </span>
                                <span className={classes.courseTopStat}>
                                    <DownloadsIcon
                                        className={classes.courseTopStatIcon}
                                    />
                                    {courseDetails.downloadableMaterial}{' '}
                                    downloadable materials
                                </span>
                                {showStudentCount && (
                                    <span className={classes.courseTopStat}>
                                        <StudentsIcon
                                            className={
                                                classes.courseTopStatIcon
                                            }
                                        />
                                        {courseDetails.enrolled} students
                                        enrolled
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    {paidUser && (
                        <div className={classes.courseCardBottomMobile}>
                            <UserChoices
                                setUserChoice={setUserChoice}
                                userChoice={userChoice}
                                mobile
                            />
                        </div>
                    )}
                    {!hasFree && (
                        <div className={classes.courseCardBottomMobile}>
                            <EarnPoints courseDetails={courseDetails} mobile />
                        </div>
                    )}
                    <div className={classes.courseDescription}>
                        <img
                            src={arrow}
                            alt="Arrow"
                            className={classes.arrowIcon}
                        />
                        <div
                            className={classNames(
                                classes.courseCardBottom,
                                hasFree && classes.courseCardBottomNoPadding
                            )}
                        >
                            {/*
                                If the course is free
                             */}
                            {hasFree ? (
                                <>
                                    <p className={classes.courseCardBottomFree}>
                                        You have
                                        <span
                                            className={
                                                classes.courseCardBottomFreeNums
                                            }
                                        >
                                            {' '}
                                            {userFreeCourses}{' '}
                                        </span>
                                        Free Courses left for this month
                                    </p>
                                    <FreePrice courseDetails={courseDetails} />
                                    <span
                                        className={classNames(
                                            classes.courseCardBottomSplitLine,
                                            classes.courseCardBottomSplitLineNotFull
                                        )}
                                    />
                                    <Button
                                        className={
                                            classes.courseCardBottomFreeButton
                                        }
                                        bold={false}
                                        onClick={() => buyNow({ free: true })}
                                    >
                                        Get For Free
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <p className={classes.courseCardPrice}>
                                        {convertToFormatted(
                                            courseDetails.price,
                                            false,
                                            true
                                        )}
                                    </p>
                                    {subscribedUser && (
                                        <EarnPoints
                                            courseDetails={courseDetails}
                                        />
                                    )}
                                    <span
                                        className={
                                            classes.courseCardBottomSplitLine
                                        }
                                    />
                                    {subscribedUser && (
                                        <BuyNowSnippet
                                            addToCart={addToCart}
                                            buyNow={buyNow}
                                            hasEnrolled={
                                                courseDetails.isEnrolled
                                            }
                                        />
                                    )}
                                    {paidUser && (
                                        <>
                                            <UserChoices
                                                userChoice={userChoice}
                                                setUserChoice={setUserChoice}
                                            />
                                            {userChoice === 'purchase' && (
                                                <BuyNowSnippet
                                                    addToCart={addToCart}
                                                    buyNow={buyNow}
                                                    hasEnrolled={
                                                        courseDetails.isEnrolled
                                                    }
                                                />
                                            )}
                                            {userChoice === 'upgrade' && (
                                                <Button
                                                    bold={false}
                                                    onClick={upgradePlan}
                                                >
                                                    Upgrade Plan
                                                </Button>
                                            )}
                                        </>
                                    )}
                                    {!isLoggedIn && (
                                        <Button
                                            bold={false}
                                            onClick={handleLogin}
                                        >
                                            Buy Now
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                        <div className={classes.courseDescriptionText}>
                            <h1 className={classes.courseSectionHeader}>
                                Course Description
                            </h1>
                            <p className={classes.courseDescriptionTextContent}>
                                {courseDetails.description}
                            </p>
                        </div>
                    </div>
                    <div className={classes.courseObjectives}>
                        <h1 className={classes.courseSectionHeader}>
                            Course Objectives
                        </h1>
                        <div>
                            {courseDetails.objectives.map(objective => (
                                <p className={classes.courseObjective}>
                                    {objective}
                                </p>
                            ))}
                        </div>
                    </div>
                    <div className={classes.courseActionsMobile}>
                        {hasFree ? (
                            <>
                                <FreePrice courseDetails={courseDetails} />
                                <Button
                                    className={
                                        classes.courseCardBottomFreeButton
                                    }
                                    bold={false}
                                    onClick={() => buyNow({ free: true })}
                                >
                                    Get For Free
                                </Button>
                            </>
                        ) : userChoice !== 'upgrade' ? (
                            <>
                                <p className={classes.courseCardPrice}>
                                    {convertToFormatted(
                                        courseDetails.price,
                                        false,
                                        true
                                    )}
                                </p>
                                <BuyNowSnippet
                                    buyNow={buyNow}
                                    addToCart={addToCart}
                                    hasEnrolled={courseDetails.isEnrolled}
                                />
                            </>
                        ) : (
                            <Button bold={false} onClick={upgradePlan}>
                                Upgrade Plan
                            </Button>
                        )}
                    </div>
                    <div className={classes.courseContent}>
                        <h1
                            className={classNames(
                                classes.courseSectionHeader,
                                classes.courseContentHeader
                            )}
                        >
                            Course Content
                        </h1>
                        <div className={classes.courseContentRows}>
                            {courseDetails?.courseLessons?.map(lesson => (
                                <CourseContent
                                    title={lesson.title}
                                    lectures={
                                        lesson.lessonTopics?.map(
                                            topic => topic.title
                                        ) || []
                                    }
                                />
                            ))}
                        </div>
                    </div>
                    <div className={classes.relatedCourses}>
                        <h1 className={classes.relatedCoursesHeader}>
                            Related Courses
                        </h1>
                        <div className={classes.relatedCoursesGrid}>
                            {courseDetails?.relatedCourses?.map(course => (
                                <CourseCard
                                    className={classes.relatedCourse}
                                    title={course.title}
                                    category={course.catName}
                                    price={course.price}
                                    attendees={course.enrolled}
                                    url={
                                        course.thumbnail
                                            ? getImageURL(course.thumbnail)
                                            : placeholder
                                    }
                                    coursecode={course.coursecode}
                                    courseType={course.courseType}
                                    premium={
                                        course.courseType ===
                                        CourseTypes.Premium
                                    }
                                />
                            ))}
                        </div>
                        {courseDetails?.relatedCourses?.length === 0 && (
                            <h4 className={classes.relatedCoursesEmpty}>
                                No Related Courses Found
                            </h4>
                        )}
                    </div>
                </main>
            )}
            <Footer />
        </>
    )
}

export default CourseDetails
